// src/components/NotesDialog.jsx
// My notes (3.37.0): a private notebook from the menu under your name. A
// note is plain text or a checklist (tick items off as you go). Notes save
// by themselves as you type, belong to your login only, and nobody else
// can see them, not even a Super Admin. During View as you still see your
// own notes (the server ignores View as here).
//
// 3.74.0 (owner, Oct 30):
//   - Undo: deleting a note or removing a checklist item shows "… · Undo"
//     for a few seconds (no "Delete this note?" question any more); ↩ Undo
//     and ↪ Redo (Ctrl+Z / Ctrl+Shift+Z) step back through this note's
//     changes while the window is open.
//   - Recently deleted: deleted notes stay 30 days (Restore / Delete
//     forever), then the server removes them.
//   - Reminders: ⏰ on a note or a checklist item; when it's due it comes to
//     the bell (and the phone, without the note's text), from the server's
//     5-minute job.
//   - Checklist tools: drag ⋮⋮ (or Alt+↑/↓) to reorder, Untick all, Clear
//     ticked items.
//
// 3.76.0 (owner, Nov 1: My notes extras):
//   - Formatting in text notes: bold, italic, underline, lists (the cover
//     message's editor); html is saved with the plain text.
//   - Colour labels, and a filter by colour or Templates above the list.
//   - Duplicate; Use as a template (＋ From a template starts a new note
//     from it: items unticked, no reminders).
//   - Faxes in a note (the fax panel's "Add to my notes"): click one to open
//     it, if you can still see that fax.
//
// 3.77.0 (owner, Nov 2: sharing): 👥 Share picks people and Can view / Can
// edit for each (business clients: people at the same company; individual
// clients: any address, and people without a login get an email link with
// a code). Notes shared with you show "👥 <who>" and open read-only or
// editable; only the owner reminds, colours, pins, shares or deletes. Shared
// notes refresh every 30 seconds, and a save over someone else's newer
// change is refused and shows their version (backend 409).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import MenuDialog, { MD } from './MenuDialog';
import Emo from './Emo';
import { fetchNotes, addNote, saveNote, deleteNote, fetchDeletedNotes, restoreNote, deleteNoteForever, copyNote, fetchFaxRow, fetchSharePeople, saveNoteShares, leaveSharedNote } from '../utils/api';
import { formatShortDate, formatFax } from '../utils/format';
import { RichText } from './EmailEditorParts';
import { cleanPaste, fromPlain, toText } from '../utils/coverMessage';
import FaxDetailPanel from './FaxDetailPanel';
import { PILL, PILL_ON, PILL_ROW } from '../theme/tabs';
import { timeZone, tzLabel, wallToIso, isoToWall } from '../utils/timeZone';
import { usePhone } from '../hooks/useWindowWidth';
import Loader from './Loader';

const newId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Date.now() + Math.random()));
const firstLine = s => String(s || '').split('\n').find(l => l.trim()) || '';
const titleOf = n => n.title?.trim() || (n.kind === 'list' ? n.items?.[0]?.text : firstLine(n.text)) || 'Untitled note';
const progress = n => (n.kind === 'list' && n.items?.length ? `${n.items.filter(i => i.done).length} of ${n.items.length} done` : null);
const CONTENT = ['kind', 'title', 'text', 'html', 'items', 'pinned', 'remindAt', 'color', 'template', 'links'];
// 3.76.0: colour labels (the server keeps only these)
export const NOTE_COLORS = [['red', 'Red', 'var(--c-e53935)'], ['orange', 'Orange', 'var(--c-fb8c00)'], ['yellow', 'Yellow', 'var(--c-f9a825)'],
  ['green', 'Green', 'var(--c-43a047)'], ['blue', 'Blue', 'var(--c-1e88e5)'], ['purple', 'Purple', 'var(--c-8e24aa)']];
const isMine = n => !n.access || n.access === 'owner';
const sharedText = n => (isMine(n) ? (n.shares?.length ? `Shared with ${n.shares.length}` : '') : `From ${n.sharedBy?.name || n.sharedBy?.email || 'someone'}`);
const colorOf = c => NOTE_COLORS.find(x => x[0] === c)?.[2] || null;
const Dot = ({ c, size = 10, ring = false }) => (colorOf(c) ? <span aria-hidden="true" style={{ display: 'inline-block', width: size, height: size, borderRadius: '50%', background: colorOf(c), flexShrink: 0, ...(ring ? { boxShadow: '0 0 0 2px var(--c-ffffff)' } : {}) }} /> : null);
// A small menu closes on a click outside it (its wrapper has data-pop)
function useCloseOutside(open, close) {
  useEffect(() => {
    if (!open) return undefined;
    const onDown = e => { if (!e.target.closest?.('[data-pop]')) close(); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, close]);
}
const POP = { position: 'absolute', marginTop: 6, zIndex: 5, background: 'var(--c-ffffff)', border: '1px solid var(--c-e0e0e0)', borderRadius: 10, boxShadow: '0 10px 30px rgb(var(--c-000000-rgb) / 0.18)', padding: 6, display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 280, overflowY: 'auto' };
const MENU_ITEM = { display: 'flex', alignItems: 'center', gap: 6, width: '100%', textAlign: 'left', background: 'none', border: 'none', borderRadius: 6, padding: '7px 9px', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, color: 'var(--t-263238)' };
// "To (555) 010-0100 · Oct 30" / "From …"
const dayOf = iso => new Date(iso).toLocaleDateString('en-US', { timeZone: timeZone(), month: 'short', day: 'numeric' });
export const faxLinkText = l => `${l.type === 'incoming' ? 'From' : 'To'} ${l.number ? formatFax(l.number) : 'a fax'}${l.at ? ` · ${dayOf(l.at)}` : ''}${l.failed ? ' · failed' : ''}`;
const contentOf = n => Object.fromEntries(CONTENT.map(k => [k, n[k] ?? null]));
// A reminder time in the person's own time zone (Preferences → My profile)
export const reminderText = iso => `${new Date(iso).toLocaleString('en-US', { timeZone: timeZone(), weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} ${tzLabel(new Date(iso))}`;
const due = n => [n.remindAt && !n.remindedAt ? n.remindAt : null, ...(n.kind === 'list' ? (n.items || []).filter(i => i.remindAt && !i.remindedAt && !i.done).map(i => i.remindAt) : [])].filter(Boolean).sort()[0] || null;

export default function NotesDialog({ onClose, getToken, notify, confirm, openNoteId = null }) {
  const [notes, setNotes] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [q, setQ] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState('');            // '' | 'saving' | 'saved' | 'error'
  const [binOpen, setBinOpen] = useState(false);       // Recently deleted
  const [bin, setBin] = useState(null);
  const [filter, setFilter] = useState('');            // 3.76.0: '' | a colour | 'templates'
  const [rev, setRev] = useState(0);                   // the text editor reloads after Undo / Redo
  const [fax, setFax] = useState(null);                // a linked fax open in the fax panel: { type, row }
  const [tplOpen, setTplOpen] = useState(false);       // ＋ From a template
  useCloseOutside(tplOpen, useCallback(() => setTplOpen(false), []));
  const phone = usePhone();
  const timers = useRef({});
  const notesRef = useRef(notes);
  notesRef.current = notes;
  // Undo / Redo: each note's earlier versions while the window is open
  const history = useRef({});                          // id → { past: [], future: [], at }

  // 3.77.0: the server time of each note as last loaded or saved (a save
  // over someone else's newer change is refused)
  const baseAt = useRef({});
  useEffect(() => {
    fetchNotes(getToken).then(j => {
      (j.data || []).forEach(n => { baseAt.current[n.id] = n.updatedAt; });
      setNotes(j.data || []);
      const want = openNoteId && j.data?.find(n => n.id === openNoteId);
      if (want) setOpenId(want.id); else if (j.data?.length) setOpenId(j.data[0].id);
    }).catch(e => { setError(e.message); setNotes([]); });
  }, [getToken, openNoteId]);
  // Anything still waiting to save goes now when the window closes
  useEffect(() => () => Object.values(timers.current).forEach(t => t.flush?.()), []);

  const open = notes?.find(n => n.id === openId) || null;
  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (notes || []).filter(n => !t || [n.title, n.text, ...(n.items || []).map(i => i.text)].join('\n').toLowerCase().includes(t))
      .filter(n => !filter || (filter === 'templates' ? n.template : filter === 'shared' ? !!sharedText(n) : n.color === filter))
      .sort((a, b) => (b.pinned - a.pinned) || String(b.updatedAt).localeCompare(String(a.updatedAt)));
  }, [notes, q, filter]);
  const templates = (notes || []).filter(n => n.template && isMine(n));
  const shownIds = shown.map(n => n.id);
  // 3.37.5: something is always open while there are notes to show (after a
  // delete, or when the search hides the open one): the first in the list
  useEffect(() => {
    if (!notes?.length || (open && shownIds.includes(open.id))) return;
    if (shownIds.length) setOpenId(shownIds[0]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes?.length, open?.id, shownIds.join(',')]);

  async function create(kind) {
    try {
      const { note } = await addNote(kind === 'list' ? { kind, title: '', items: [{ id: newId(), text: '', done: false }] } : { kind, title: '', text: '' }, getToken);
      setNotes(ns => [note, ...(ns || [])]);
      setOpenId(note.id); setQ(''); setBinOpen(false);
    } catch (e) { notify({ tone: 'error', title: 'Couldn’t add a note', detail: e.message }); }
  }

  // Change a note here at once; save it a moment after the typing stops.
  // record: false for Undo / Redo themselves. Typing within a second and a
  // half counts as one step, so Undo takes back a burst of typing at once.
  const change = useCallback((id, patch, { record = true } = {}) => {
    const before = notesRef.current?.find(n => n.id === id);
    if (before?.access === 'view') return;   // 3.77.0: shared with you to view only
    if (record && before) {
      const h = history.current[id] || (history.current[id] = { past: [], future: [], at: 0, key: '' });
      const key = Object.keys(patch).sort().join(',');
      const typing = (key === 'text' || key === 'title' || key === 'html,text' || (key === 'items' && patch.items?.length === before.items?.length && patch.items.every((x, i) => x.id === before.items[i]?.id && x.done === before.items[i]?.done)));
      if (!(typing && h.key === key && Date.now() - h.at < 1500)) h.past = [...h.past, contentOf(before)].slice(-100);
      h.future = []; h.at = Date.now(); h.key = typing ? key : '';
    }
    // notesRef follows at once too, so a save straight after (closing the window) has the latest
    const next = ns => ns.map(n => (n.id === id ? { ...n, ...patch, updatedAt: new Date().toISOString() } : n));
    notesRef.current = next(notesRef.current || []);
    setNotes(next);
    setSaving('saving');
    const prev = timers.current[id];
    if (prev) clearTimeout(prev.t);
    const run = async () => {
      delete timers.current[id];
      const current = notesRef.current?.find(n => n.id === id);
      if (!current) return;
      try {
        const { note } = await saveNote(id, { ...contentOf(current), base: baseAt.current[id] || null }, getToken);
        if (note) baseAt.current[id] = note.updatedAt;
        // The server keeps which reminders were sent; take those back
        if (note) setNotes(ns => ns.map(n => (n.id === id ? { ...n, remindedAt: note.remindedAt, items: (n.items || []).map(i => ({ ...i, remindedAt: note.items?.find(x => x.id === i.id)?.remindedAt ?? null })) } : n)));
        setSaving(Object.keys(timers.current).length ? 'saving' : 'saved');
        window.dispatchEvent(new Event('notes-changed'));   // 3.74.2: the bell learns the next reminder
      } catch (e) {
        // 3.77.0: someone else saved first: show their version
        if (e.status === 409 && e.data?.note) {
          const latest = e.data.note;
          baseAt.current[id] = latest.updatedAt;
          notesRef.current = (notesRef.current || []).map(n => (n.id === id ? latest : n));
          setNotes(ns => ns.map(n => (n.id === id ? latest : n)));
          delete history.current[id]; setRev(r => r + 1); setSaving('');
          notify({ tone: 'warning', title: 'Someone else changed this note', detail: 'You’re seeing their latest version. Please make your last change again.' });
          return;
        }
        setSaving('error'); notify({ tone: 'error', title: 'Couldn’t save your note', detail: e.message });
      }
    };
    timers.current[id] = { t: setTimeout(run, 700), flush: run };
  }, [getToken, notify]);

  // 3.77.0: shared notes change under you: take in newer versions (not
  // while yours is waiting to save) and notes shared or unshared.
  // 3.77.3 (owner: a change made on the shared page only showed after a
  // refresh): the newer version was put in notesRef but the screen kept
  // the old one (merge ran twice); now one merged list for both. Checked
  // every 15 seconds while visible, and at once when the portal tab or
  // window comes back to the front.
  const anyShared = (notes || []).some(n => !isMine(n) || n.shares?.length);
  useEffect(() => {
    if (!anyShared) return undefined;
    let busy = false;
    const check = () => {
      if (document.hidden || busy) return;
      busy = true;
      fetchNotes(getToken).then(j => {
        const fresh = j.data || [];
        const byId = new Map(fresh.map(n => [n.id, n]));
        let reload = false;
        const kept = (notesRef.current || []).filter(n => isMine(n) || byId.has(n.id)).map(n => {
          const f = byId.get(n.id);
          if (!f || timers.current[n.id] || !(f.updatedAt > (baseAt.current[n.id] || ''))) return f && isMine(n) ? { ...n, shares: f.shares, opens: f.opens } : n;
          baseAt.current[n.id] = f.updatedAt;
          delete history.current[n.id];   // their change isn't one of yours to undo
          if (n.id === openIdRef.current) reload = true;
          return f;
        });
        const added = fresh.filter(f => !kept.some(n => n.id === f.id));
        added.forEach(f => { baseAt.current[f.id] = f.updatedAt; });
        const next = [...kept, ...added];
        notesRef.current = next;
        setNotes(next);
        if (reload) setRev(r => r + 1);
      }).catch(() => {}).finally(() => { busy = false; });
    };
    const tick = setInterval(check, 15000);
    const onBack = () => { if (!document.hidden) check(); };
    document.addEventListener('visibilitychange', onBack);
    window.addEventListener('focus', onBack);
    return () => { clearInterval(tick); document.removeEventListener('visibilitychange', onBack); window.removeEventListener('focus', onBack); };
  }, [anyShared, getToken]);
  const openIdRef = useRef(openId);
  openIdRef.current = openId;

  // Take a note shared with you out of your list
  async function leave(n) {
    const ok = await confirm({ title: 'Remove from your list?', message: `“${titleOf(n)}” was shared with you by ${n.sharedBy?.name || n.sharedBy?.email}. It stays with them; you won’t see it any more unless they share it again.`, confirmLabel: 'Remove' });
    if (!ok) return;
    try {
      await leaveSharedNote(n.id, getToken);
      setNotes(ns => ns.filter(x => x.id !== n.id));
      setOpenId(null);
      notify({ tone: 'info', title: 'Removed from your list', detail: `“${titleOf(n)}”` });
    } catch (e) { notify({ tone: 'error', title: 'Couldn’t remove it', detail: e.message }); }
  }
  // The Share box saved: the note's new list of people
  const sharesSaved = note => { setNotes(ns => ns.map(n => (n.id === note.id ? { ...n, shares: note.shares, opens: note.opens } : n))); };

  const canUndo = !!(open && history.current[open.id]?.past.length);
  const canRedo = !!(open && history.current[open.id]?.future.length);
  const undo = useCallback(() => {
    const n = notesRef.current?.find(x => x.id === openId); const h = history.current[openId];
    if (!n || !h?.past.length) return;
    h.future = [contentOf(n), ...h.future]; const back = h.past[h.past.length - 1]; h.past = h.past.slice(0, -1); h.key = '';
    change(n.id, back, { record: false }); setRev(r => r + 1);
  }, [openId, change]);
  const redo = useCallback(() => {
    const n = notesRef.current?.find(x => x.id === openId); const h = history.current[openId];
    if (!n || !h?.future.length) return;
    h.past = [...h.past, contentOf(n)]; const fwd = h.future[0]; h.future = h.future.slice(1); h.key = '';
    change(n.id, fwd, { record: false }); setRev(r => r + 1);
  }, [openId, change]);
  // Ctrl+Z / Cmd+Z, Ctrl+Shift+Z / Ctrl+Y, anywhere in the window
  useEffect(() => {
    const onKey = e => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
      const k = e.key.toLowerCase();
      if (k === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
      else if ((k === 'z' && e.shiftKey) || k === 'y') { e.preventDefault(); redo(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo]);

  // Delete: to Recently deleted, with Undo for a few seconds (no question)
  async function remove(n) {
    try {
      if (timers.current[n.id]) { await timers.current[n.id].flush(); }
      await deleteNote(n.id, getToken);
      // 3.37.5: open the next note in the list (or the one before), not an empty page
      const i = shown.findIndex(x => x.id === n.id);
      const next = shown[i + 1] || shown[i - 1] || null;
      setNotes(ns => ns.filter(x => x.id !== n.id));
      setOpenId(next?.id || null);
      setBin(null);
      window.dispatchEvent(new Event('notes-changed'));
      notify({ tone: 'info', icon: '🗑', title: 'Note deleted', detail: `“${titleOf(n)}” is in Recently deleted for 30 days.`, hint: '↩ Undo', onClick: () => restore(n.id) });
    } catch (e) { notify({ tone: 'error', title: 'Couldn’t delete it', detail: e.message }); }
  }
  async function restore(id) {
    try {
      const { note } = await restoreNote(id, getToken);
      setNotes(ns => [note, ...(ns || []).filter(x => x.id !== id)]);
      setBin(b => (b ? b.filter(x => x.id !== id) : b));
      setOpenId(id); setQ(''); setBinOpen(false);
      window.dispatchEvent(new Event('notes-changed'));
      notify({ tone: 'success', title: 'Note restored', detail: `“${titleOf(note)}” is back in My notes.` });
    } catch (e) { notify({ tone: 'error', title: 'Couldn’t restore it', detail: e.message }); }
  }
  async function forever(n) {
    const ok = await confirm({ title: 'Delete forever?', message: `“${titleOf(n)}” will be gone for good. This can’t be undone.`, confirmLabel: 'Delete forever', danger: true });
    if (!ok) return;
    try { await deleteNoteForever(n.id, getToken); setBin(b => b.filter(x => x.id !== n.id)); }
    catch (e) { notify({ tone: 'error', title: 'Couldn’t delete it', detail: e.message }); }
  }
  useEffect(() => {
    if (!binOpen || bin) return;
    fetchDeletedNotes(getToken).then(j => setBin(j.data || [])).catch(e => { setBin([]); notify({ tone: 'error', title: 'Couldn’t load Recently deleted', detail: e.message }); });
  }, [binOpen, bin, getToken, notify]);

  // A checklist item removed: Undo puts it back where it was
  function removeItem(n, it) {
    const i = (n.items || []).findIndex(x => x.id === it.id);
    change(n.id, { items: n.items.filter(x => x.id !== it.id) });
    notify({ tone: 'info', icon: '🗑', title: 'Item removed', detail: it.text || 'An empty item', hint: '↩ Undo', onClick: () => {
      const cur = notesRef.current?.find(x => x.id === n.id); if (!cur || cur.items.some(x => x.id === it.id)) return;
      const items = [...cur.items]; items.splice(Math.min(i, items.length), 0, it); change(n.id, { items });
    } });
  }

  // Clear ticked items: Undo puts them all back
  function clearTicked(n) {
    const before = n.items || [];
    const gone = before.filter(x => x.done);
    change(n.id, { items: before.filter(x => !x.done) });
    notify({ tone: 'info', icon: '🗑', title: `${gone.length} ticked item${gone.length === 1 ? '' : 's'} cleared`, hint: '↩ Undo', onClick: () => {
      const cur = notesRef.current?.find(x => x.id === n.id); if (!cur) return;
      change(n.id, { items: [...cur.items, ...gone.filter(g => !cur.items.some(x => x.id === g.id))] });
    } });
  }

  // Text ↔ checklist: lines become items and items become lines
  function convert(n) {
    if (n.kind === 'text') change(n.id, { kind: 'list', html: '', items: String(n.text || '').split('\n').filter(l => l.trim()).map(l => ({ id: newId(), text: l.replace(/^\s*[-*•]\s*/, ''), done: false })).concat(n.text?.trim() ? [] : [{ id: newId(), text: '', done: false }]) });
    else { const text = (n.items || []).map(i => `${i.done ? '✓ ' : ''}${i.text}`).join('\n'); change(n.id, { kind: 'text', text, html: fromPlain(text) }); }
    setRev(r => r + 1);
  }

  // 3.76.0: Duplicate, or a new note from a template
  async function copy(n, fromTemplate = false) {
    try {
      if (timers.current[n.id]) await timers.current[n.id].flush();
      const { note } = await copyNote(n.id, fromTemplate, getToken);
      setNotes(ns => [note, ...(ns || [])]);
      setOpenId(note.id); setQ(''); setFilter(''); setBinOpen(false); setTplOpen(false);
      notify({ tone: 'success', title: fromTemplate ? 'New note from the template' : 'Note duplicated', detail: `“${titleOf(note)}” is open.` });
    } catch (e) { notify({ tone: 'error', title: 'Couldn’t copy it', detail: e.message }); }
  }
  // Escape with a fax open closes only the fax (both windows hear the key)
  const faxClosedAt = useRef(0);
  const closeFax = () => { faxClosedAt.current = Date.now(); setFax(null); };
  const closeNotes = () => { if (fax || Date.now() - faxClosedAt.current < 400) closeFax(); else onClose(); };
  // 3.76.0: a fax in the note opens in the fax panel, if you can still see it
  async function openFax(l) {
    try { const j = await fetchFaxRow(l.type, l.faxId, getToken); setFax({ type: j.type, row: j.row }); }
    catch (e) { notify({ tone: 'error', title: 'Couldn’t open this fax', detail: e.message }); }
  }

  const listBtn = { display: 'block', width: '100%', textAlign: 'left', border: 'none', borderRadius: 8, padding: '8px 10px', marginBottom: 4, cursor: 'pointer', fontFamily: 'inherit' };
  const list = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minHeight: 0, ...(phone ? {} : { width: 260, flexShrink: 0, borderRight: '1px solid var(--c-eceff1)', paddingRight: 14 }) }}>
      <div style={{ display: 'flex', gap: 6 }}>
        <button onClick={() => create('text')} style={{ ...MD.primary, padding: '8px 12px', fontSize: 13, flex: 1 }}>＋ Note</button>
        <button onClick={() => create('list')} style={{ ...MD.secondary, padding: '8px 12px', fontSize: 13, flex: 1 }}><Emo e="☑" /> Checklist</button>
      </div>
      {/* 3.76.0: a new note from one of your templates */}
      {templates.length > 0 && <div data-pop style={{ position: 'relative' }}>
        <button onClick={() => setTplOpen(o => !o)} aria-expanded={tplOpen} style={{ ...MD.secondary, padding: '7px 12px', fontSize: 12.5, width: '100%' }}><Emo e="📋" /> ＋ From a template</button>
        {tplOpen && <div role="menu" aria-label="Your templates" style={{ ...POP, left: 0, right: 0, top: '100%' }} onKeyDown={e => { if (e.key === 'Escape') { e.stopPropagation(); setTplOpen(false); } }}>
          {templates.map(n => <button key={n.id} role="menuitem" onClick={() => copy(n, true)} style={MENU_ITEM}><Dot c={n.color} /> {n.kind === 'list' && <Emo e="☑" />} {titleOf(n)}</button>)}
        </div>}
      </div>}
      <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search your notes" aria-label="Search your notes" style={{ ...MD.input, padding: '7px 10px', fontSize: 13 }} />
      {/* 3.76.0: show one colour, or your templates. 3.77.0 (owner): the
          portal's shared filter pills (theme/tabs.js + the sliding highlight),
          so every state looks like the other pill rows */}
      <div data-pills role="group" aria-label="Show" style={{ ...PILL_ROW, gap: 5, alignItems: 'center' }}>
        <button aria-pressed={!filter} onClick={() => setFilter('')} style={{ ...PILL, padding: '4px 10px', fontSize: 12, ...(!filter ? PILL_ON : {}) }}>All</button>
        {NOTE_COLORS.map(([k, label]) => <button key={k} aria-pressed={filter === k} aria-label={label} title={label} onClick={() => setFilter(filter === k ? '' : k)}
          style={{ ...PILL, padding: '5px 6px', lineHeight: 0, ...(filter === k ? PILL_ON : {}) }}><Dot c={k} size={12} ring /></button>)}
        <button aria-pressed={filter === 'templates'} onClick={() => setFilter(filter === 'templates' ? '' : 'templates')} style={{ ...PILL, padding: '4px 11px', fontSize: 12, ...(filter === 'templates' ? PILL_ON : {}) }}><Emo e="📋" /> Templates</button>
        <button aria-pressed={filter === 'shared'} onClick={() => setFilter(filter === 'shared' ? '' : 'shared')} style={{ ...PILL, padding: '4px 11px', fontSize: 12, ...(filter === 'shared' ? PILL_ON : {}) }}><Emo e="👥" /> Shared</button>
      </div>
      <div style={{ overflowY: 'auto', minHeight: 0, flex: 1, maxHeight: phone ? 220 : 'none' }} role="listbox" aria-label="Your notes">
        {notes && !shown.length && <div style={{ ...MD.muted, padding: '8px 2px' }}>{q || filter ? (filter === 'templates' && !q ? 'No templates yet. Open a note and choose ⋯ More → Use as a template.' : filter === 'shared' && !q ? 'No shared notes. Open one of yours and click 👥 Share.' : 'No notes match.') : 'No notes yet. Add one above.'}</div>}
        {shown.map(n => {
          const next = due(n);
          return (
            <button key={n.id} role="option" aria-selected={!binOpen && n.id === openId} onClick={() => { setOpenId(n.id); setBinOpen(false); }}
              style={{ ...listBtn, background: !binOpen && n.id === openId ? 'var(--c-e0f2f1)' : 'transparent', boxShadow: !binOpen && n.id === openId ? 'inset 3px 0 0 var(--c-00897b)' : 'none' }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--t-263238)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}><Dot c={n.color} /><span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{n.pinned && <><Emo e="📌" /> </>}{n.kind === 'list' && <><Emo e="☑" /> </>}{titleOf(n)}</span></div>
              <div style={{ fontSize: 11.5, color: 'var(--t-90a4ae)' }}>{[sharedText(n) && `👥 ${sharedText(n)}`, n.template && 'Template', progress(n), n.links?.length && `${n.links.length} fax${n.links.length === 1 ? '' : 'es'}`, formatShortDate(n.updatedAt)].filter(Boolean).join(' · ')}</div>
              {next && <div style={{ fontSize: 11.5, color: 'var(--t-00796b)', fontWeight: 600 }}><Emo e="⏰" /> {reminderText(next)}</div>}
            </button>
          );
        })}
      </div>
      <button onClick={() => setBinOpen(o => !o)} aria-pressed={binOpen}
        style={{ ...MD.secondary, padding: '7px 10px', fontSize: 12.5, border: '1px dashed var(--c-b0bec5)', color: 'var(--t-546e7a)', ...(binOpen ? { background: 'var(--c-eceff1)' } : {}) }}>
        <Emo e="🗑" /> Recently deleted
      </button>
    </div>
  );

  const binView = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0 }}>
      <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--t-263238)' }}><Emo e="🗑" /> Recently deleted</div>
      <div style={MD.muted}>Deleted notes stay here for 30 days, then they’re removed for good. Restore one to bring it back.</div>
      <div style={{ overflowY: 'auto', minHeight: 0, flex: 1 }}>
        {!bin ? <Loader size="sm" /> : !bin.length ? <div style={{ ...MD.muted, padding: '18px 2px' }}>Nothing here. Notes you delete wait here for 30 days.</div>
          : bin.map(n => {
            const left = Math.max(1, 30 - Math.floor((Date.now() - Date.parse(n.deletedAt)) / 86400000));
            return (
              <div key={n.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 4px', borderBottom: '1px solid var(--c-eceff1)', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 160 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--t-263238)' }}>{n.kind === 'list' && <><Emo e="☑" /> </>}{titleOf(n)}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--t-90a4ae)' }}>Deleted {formatShortDate(n.deletedAt)} · removed for good in {left} day{left === 1 ? '' : 's'}</div>
                </div>
                <button onClick={() => restore(n.id)} style={{ ...MD.secondary, padding: '6px 12px', fontSize: 12 }}>↩ Restore</button>
                <button onClick={() => forever(n)} style={{ ...MD.danger, padding: '6px 12px', fontSize: 12 }}>Delete forever</button>
              </div>
            );
          })}
      </div>
    </div>
  );

  return (
    <MenuDialog id="notes" icon="📝" title="My notes" onClose={closeNotes} width={980} height={phone ? '90vh' : '80vh'}
      sub="Only you can see these, unless you share one. They're saved to your login, so they're here on any computer."
      headerExtra={<span style={{ fontSize: 12, color: saving === 'error' ? 'var(--t-c62828)' : 'var(--t-90a4ae)', alignSelf: 'center', whiteSpace: 'nowrap' }}>{saving === 'saving' ? 'Saving…' : saving === 'saved' ? '✓ Saved' : saving === 'error' ? 'Not saved' : ''}</span>}
      bodyStyle={{ display: 'flex', flexDirection: phone ? 'column' : 'row', gap: 16, overflow: phone ? 'auto' : 'hidden' }}>
      {!notes ? <Loader size="sm" /> : <>
        {list}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {error && <div style={MD.error}>{error}</div>}
          {binOpen ? binView
            : !open ? <div style={{ ...MD.muted, margin: 'auto', textAlign: 'center' }}>{notes.length
              ? 'No notes match your search.'
              : <>No notes yet. Add a note or a checklist.<br />Handy for to-do lists, ideas and reminders.</>}</div>
            : <Editor key={open.id} note={open} change={change} convert={convert} remove={remove} removeItem={removeItem} clearTicked={clearTicked}
                undo={undo} redo={redo} canUndo={canUndo} canRedo={canRedo} rev={rev} copy={copy} openFax={openFax} notify={notify}
                leave={leave} getToken={getToken} sharesSaved={sharesSaved} />}
        </div>
      </>}
      {fax && <FaxDetailPanel type={fax.type} row={fax.row} inNotes onClose={closeFax} />}
    </MenuDialog>
  );
}

// ⏰ The reminder picker: quick choices, or a day and time in your time zone
function ReminderPicker({ value, sent, onSet, onClose }) {
  const tz = timeZone();
  const [wall, setWall] = useState(value ? isoToWall(value, tz) : '');
  const at = (days, hour, weekday = null) => {
    const now = new Date(); const today = isoToWall(now.toISOString(), tz).slice(0, 10);
    const d = new Date(`${today}T12:00:00Z`);
    if (weekday != null) { const cur = d.getUTCDay(); d.setUTCDate(d.getUTCDate() + (((weekday - cur + 7) % 7) || 7)); }
    else d.setUTCDate(d.getUTCDate() + days);
    return wallToIso(`${d.toISOString().slice(0, 10)}T${String(hour).padStart(2, '0')}:00`, tz);
  };
  const laterToday = (() => { const h = Number(isoToWall(new Date().toISOString(), tz).slice(11, 13)); return h < 16 ? at(0, Math.max(h + 2, 13)) : null; })();
  const quick = [laterToday && ['Later today', laterToday], ['Tomorrow 9 AM', at(1, 9)], ['Monday 9 AM', at(0, 9, 1)]].filter(Boolean);
  const set = iso => { if (iso && Date.parse(iso) > Date.now() - 60000) { onSet(iso); onClose(); } };
  return (
    <div role="dialog" aria-label="Remind me" style={{ position: 'absolute', right: 0, top: '100%', marginTop: 6, zIndex: 5, width: 280, background: 'var(--c-ffffff)', border: '1px solid var(--c-e0e0e0)', borderRadius: 10, boxShadow: '0 10px 30px rgb(var(--c-000000-rgb) / 0.18)', padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}
      onKeyDown={e => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--t-263238)' }}><Emo e="⏰" /> Remind me</div>
      {value && <div style={{ fontSize: 12, color: sent ? 'var(--t-90a4ae)' : 'var(--t-00796b)' }}>{sent ? 'Reminded' : 'Set for'} {reminderText(value)}</div>}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {quick.map(([label, iso]) => <button key={label} type="button" onClick={() => set(iso)} style={{ ...MD.secondary, padding: '5px 9px', fontSize: 12 }} title={reminderText(iso)}>{label}</button>)}
      </div>
      <label style={{ fontSize: 12, color: 'var(--t-546e7a)', display: 'flex', flexDirection: 'column', gap: 4 }}>Or pick a day and time ({tzLabel()})
        <input type="datetime-local" value={wall} onChange={e => setWall(e.target.value)} min={isoToWall(new Date().toISOString(), tz)} style={{ ...MD.input, padding: '6px 8px', fontSize: 13 }} />
      </label>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        {value && <button type="button" onClick={() => { onSet(null); onClose(); }} style={{ ...MD.danger, padding: '5px 10px', fontSize: 12 }}>Remove</button>}
        <button type="button" onClick={onClose} style={{ ...MD.secondary, padding: '5px 10px', fontSize: 12 }}>Cancel</button>
        <button type="button" disabled={!wall} onClick={() => set(wallToIso(wall, tz))} style={{ ...MD.primary, padding: '5px 12px', fontSize: 12, opacity: wall ? 1 : 0.5 }}>Set</button>
      </div>
    </div>
  );
}

// 3.77.0: 👥 Share: who sees this note, and whether they can change it.
// Changes apply with Save; people without a portal login get the email
// link (backend lib/noteShares.js decides who may be added).
function ShareBox({ note, getToken, notify, onSaved, onClose }) {
  const [draft, setDraft] = useState(() => (note.shares || []).map(x => ({ email: x.email, name: x.name, can: x.can, outside: x.outside, openedAt: x.openedAt })));
  const [opts, setOpts] = useState(null);   // { people, outsideOk }
  const [email, setEmail] = useState('');
  const [can, setCan] = useState('view');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { fetchSharePeople(getToken).then(setOpts).catch(() => setOpts({ people: [], outsideOk: false })); }, [getToken]);
  const nameOf = e => opts?.people?.find(p => p.email === e)?.name || '';
  function add() {
    const e = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { setErr('Type an email address, or pick someone from the list.'); return; }
    if (draft.some(x => x.email === e)) { setErr('That person is already on the list.'); return; }
    if (opts && !opts.outsideOk && !opts.people.some(p => p.email === e)) { setErr('You can share notes only with people at your company who use the Fax Portal.'); return; }
    setDraft(d => [...d, { email: e, name: nameOf(e), can, isNew: true }]); setEmail(''); setErr('');
  }
  async function save() {
    setBusy(true); setErr('');
    try {
      const { note: saved, added } = await saveNoteShares(note.id, draft.map(x => ({ email: x.email, can: x.can })), getToken);
      onSaved(saved);
      const out = (added || []).filter(a => a.outside).length;
      notify({ tone: 'success', icon: '👥', title: saved.shares?.length ? `Shared with ${saved.shares.length} ${saved.shares.length === 1 ? 'person' : 'people'}` : 'Not shared any more',
        detail: [(added || []).filter(a => !a.outside).length ? 'They’ll find it in My notes.' : '', out ? `We emailed a link to ${out === 1 ? 'the person' : `the ${out} people`} without a Fax Portal login.` : ''].filter(Boolean).join(' ') || undefined });
      onClose();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }
  const sel = { ...MD.input, width: 'auto', padding: '5px 8px', fontSize: 12.5 };
  // Over the whole page (the My notes window's animation would otherwise hold it)
  return createPortal(
    <div role="dialog" aria-modal="true" aria-label="Share this note" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
      onKeyDown={e => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } }}
      style={{ position: 'fixed', inset: 0, zIndex: 1400, background: 'rgb(var(--c-000000-rgb) / 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ width: 'min(480px, 100%)', maxHeight: '85vh', overflowY: 'auto', background: 'var(--c-ffffff)', borderRadius: 14, boxShadow: '0 20px 50px rgb(var(--c-000000-rgb) / 0.25)', padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--t-263238)' }}><Emo e="👥" /> Share “{titleOf(note)}”</div>
        <div style={MD.muted}>{opts?.outsideOk
          ? 'Share with anyone by email. People with a Fax Portal login find it in My notes; anyone else gets an email with a link, and a code to check it’s them.'
          : 'Share with people at your company. They find it in My notes, with a bell alert.'} Fax Portal support can’t see your notes.</div>
        {draft.length > 0 && <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {draft.map(x => (
            <div key={x.email} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '6px 0', borderBottom: '1px solid var(--c-eceff1)' }}>
              <div style={{ flex: 1, minWidth: 160 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--t-263238)' }}>{x.name || x.email}</div>
                <div style={{ fontSize: 11.5, color: 'var(--t-90a4ae)' }}>{x.name ? `${x.email} · ` : ''}{x.isNew ? 'not shared yet (Save)' : x.outside ? (x.openedAt ? `email link · opened ${formatShortDate(x.openedAt)}` : 'email link · not opened yet') : 'in My notes'}</div>
              </div>
              <select value={x.can} onChange={e => setDraft(d => d.map(y => (y.email === x.email ? { ...y, can: e.target.value } : y)))} aria-label={`What ${x.name || x.email} can do`} style={sel}>
                <option value="view">Can view</option><option value="edit">Can edit</option>
              </select>
              <button onClick={() => setDraft(d => d.filter(y => y.email !== x.email))} aria-label={`Stop sharing with ${x.name || x.email}`} title="Stop sharing with this person" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--t-90a4ae)', fontSize: 14 }}>✕</button>
            </div>
          ))}
        </div>}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <input value={email} onChange={e => { setEmail(e.target.value); setErr(''); }} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
            list="note-share-people" placeholder={opts?.people?.length ? 'Name or email' : 'Email address'} aria-label="Who to share with" style={{ ...MD.input, flex: 1, minWidth: 180, padding: '7px 10px', fontSize: 13 }} />
          <datalist id="note-share-people">{(opts?.people || []).filter(p => !draft.some(x => x.email === p.email)).map(p => <option key={p.email} value={p.email}>{p.name}</option>)}</datalist>
          <select value={can} onChange={e => setCan(e.target.value)} aria-label="What they can do" style={sel}><option value="view">Can view</option><option value="edit">Can edit</option></select>
          <button onClick={add} style={{ ...MD.secondary, padding: '6px 12px', fontSize: 12.5 }}>Add</button>
        </div>
        {err && <div role="alert" style={MD.error}>{err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: 4 }}>
          {(note.shares || []).length > 0 && <button onClick={() => setDraft([])} style={{ ...MD.danger, padding: '7px 12px', fontSize: 12.5, marginRight: 'auto' }}>Stop sharing with everyone</button>}
          <button onClick={onClose} style={{ ...MD.secondary, padding: '7px 14px', fontSize: 13 }}>Cancel</button>
          <button onClick={save} disabled={busy} style={{ ...MD.primary, padding: '7px 16px', fontSize: 13 }}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function Editor({ note, change, convert, remove, removeItem, clearTicked, undo, redo, canUndo, canRedo, rev, copy, openFax, notify, leave, getToken, sharesSaved }) {
  const mine = isMine(note), readOnly = note.access === 'view';   // 3.77.0
  const [sharing, setSharing] = useState(false);
  const itemRefs = useRef({});
  const focusNext = useRef(null);
  const [picking, setPicking] = useState(null);   // 'note' | item id | 'color' | 'more'
  useCloseOutside(picking === 'color' || picking === 'more', useCallback(() => setPicking(null), []));
  const [dragId, setDragId] = useState(null);
  useEffect(() => { if (focusNext.current) { itemRefs.current[focusNext.current]?.focus(); focusNext.current = null; } });
  const items = note.items || [];
  const setItems = next => change(note.id, { items: next });
  function addAfter(i) {
    const it = { id: newId(), text: '', done: false };
    const next = [...items.slice(0, i + 1), it, ...items.slice(i + 1)];
    focusNext.current = it.id;
    setItems(next);
  }
  // Move an item to before another one (drag), or one place up or down (Alt+↑/↓)
  function moveTo(id, beforeId) {
    if (id === beforeId) return;
    const it = items.find(x => x.id === id); const rest = items.filter(x => x.id !== id);
    const at = beforeId ? rest.findIndex(x => x.id === beforeId) : rest.length;
    setItems([...rest.slice(0, at), it, ...rest.slice(at)]);
  }
  function step(id, dir) {
    const list = items.filter(x => !x.done); const i = list.findIndex(x => x.id === id); const j = i + dir;
    if (i < 0 || j < 0 || j >= list.length) return;
    focusNext.current = id;
    moveTo(id, dir < 0 ? list[j].id : list[j + 1]?.id || null);
  }
  const open = items.filter(i => !i.done), done = items.filter(i => i.done);
  const chip = (iso, sent) => iso && <span title={sent ? 'Reminded' : 'Reminder'} style={{ fontSize: 11.5, fontWeight: 600, whiteSpace: 'nowrap', color: sent ? 'var(--t-90a4ae)' : 'var(--t-00796b)', background: sent ? 'var(--c-f5f5f5)' : 'var(--c-e0f2f1)', borderRadius: 10, padding: '2px 8px' }}><Emo e="⏰" /> {reminderText(iso)}</span>;
  const iconBtn = { background: 'none', border: 'none', color: 'var(--t-b0bec5)', cursor: 'pointer', fontSize: 14, padding: 4 };
  const row = (it) => {
    const i = items.findIndex(x => x.id === it.id);
    return (
      <div key={it.id} draggable={!it.done && !readOnly} onDragStart={e => { setDragId(it.id); e.dataTransfer.effectAllowed = 'move'; }} onDragEnd={() => setDragId(null)}
        onDragOver={e => { if (dragId && !it.done) e.preventDefault(); }} onDrop={e => { e.preventDefault(); if (dragId) moveTo(dragId, it.id); setDragId(null); }}
        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0', position: 'relative', opacity: dragId === it.id ? 0.4 : 1, borderTop: dragId && dragId !== it.id && !it.done ? '2px solid transparent' : 'none' }}>
        {!it.done && !readOnly && <span aria-hidden="true" title="Drag to move (or Alt+↑ / Alt+↓)" style={{ cursor: 'grab', color: 'var(--t-cfd8dc)', fontSize: 13, userSelect: 'none', padding: '0 2px' }}>⋮⋮</span>}
        <input type="checkbox" checked={it.done} disabled={readOnly} onChange={() => setItems(items.map(x => (x.id === it.id ? { ...x, done: !x.done } : x)))} aria-label={it.done ? 'Mark as not done' : 'Mark as done'} style={{ width: 17, height: 17, cursor: 'pointer', flexShrink: 0 }} />
        <input ref={el => { itemRefs.current[it.id] = el; }} value={it.text} placeholder="List item" maxLength={500} readOnly={readOnly}
          onChange={e => setItems(items.map(x => (x.id === it.id ? { ...x, text: e.target.value } : x)))}
          onKeyDown={e => {
            if (readOnly) return;
            if (e.key === 'Enter') { e.preventDefault(); addAfter(i); }
            if (e.key === 'Backspace' && !it.text && items.length > 1) { e.preventDefault(); const prev = items[i - 1] || items[i + 1]; focusNext.current = prev?.id; setItems(items.filter(x => x.id !== it.id)); }
            if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown') && !it.done) { e.preventDefault(); step(it.id, e.key === 'ArrowUp' ? -1 : 1); }
          }}
          style={{ ...MD.input, flex: 1, minWidth: 0, border: 'none', borderBottom: '1px solid var(--c-eceff1)', borderRadius: 0, padding: '6px 2px', textDecoration: it.done ? 'line-through' : 'none', color: it.done ? 'var(--t-90a4ae)' : 'var(--t-263238)' }} />
        {!it.done && mine && chip(it.remindAt, !!it.remindedAt)}
        {!it.done && mine && <button onClick={() => setPicking(picking === it.id ? null : it.id)} aria-label="Remind me about this item" title="Remind me" style={{ ...iconBtn, color: it.remindAt && !it.remindedAt ? 'var(--t-00796b)' : 'var(--t-b0bec5)' }}><Emo e="⏰" /></button>}
        {!readOnly && <button onClick={() => removeItem(note, it)} aria-label="Remove this item" title="Remove" style={iconBtn}>✕</button>}
        {picking === it.id && <ReminderPicker value={it.remindAt} sent={!!it.remindedAt} onClose={() => setPicking(null)}
          onSet={iso => setItems(items.map(x => (x.id === it.id ? { ...x, remindAt: iso, remindedAt: null } : x)))} />}
      </div>
    );
  };
  const tool = { ...MD.secondary, padding: '6px 10px', fontSize: 12 };
  return (
    <>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input value={note.title || ''} onChange={e => change(note.id, { title: e.target.value })} placeholder="Title" maxLength={120} aria-label="Title" readOnly={readOnly}
          style={{ ...MD.input, flex: 1, minWidth: 180, fontSize: 17, fontWeight: 700, border: 'none', borderBottom: '2px solid var(--c-e0f2f1)', borderRadius: 0, padding: '6px 2px' }} />
        {!readOnly && <>
        <button onClick={undo} disabled={!canUndo} title="Undo (Ctrl+Z)" aria-label="Undo" style={{ ...tool, opacity: canUndo ? 1 : 0.4, cursor: canUndo ? 'pointer' : 'default' }}>↩</button>
        <button onClick={redo} disabled={!canRedo} title="Redo (Ctrl+Shift+Z)" aria-label="Redo" style={{ ...tool, opacity: canRedo ? 1 : 0.4, cursor: canRedo ? 'pointer' : 'default' }}>↪</button>
        </>}
        {/* 3.77.0: a note shared with you: copy it, or take it out of your list */}
        {!mine && <>
          <button onClick={() => copy(note)} style={tool} title="Make your own copy in My notes">⧉ Copy to my notes</button>
          <button onClick={() => leave(note)} style={{ ...MD.danger, padding: '6px 10px', fontSize: 12 }}>Remove from my list</button>
        </>}
        {mine && <>
        <button onClick={() => setSharing(true)} style={{ ...tool, ...(note.shares?.length ? { background: 'var(--c-e3f2fd)' } : {}) }} title="Share this note with other people"><Emo e="👥" /> {note.shares?.length ? `Shared (${note.shares.length})` : 'Share'}</button>
        <span style={{ position: 'relative' }}>
          <button onClick={() => setPicking(picking === 'note' ? null : 'note')} style={{ ...tool, ...(note.remindAt && !note.remindedAt ? { background: 'var(--c-e0f2f1)' } : {}) }} aria-expanded={picking === 'note'}><Emo e="⏰" /> Remind me</button>
          {picking === 'note' && <ReminderPicker value={note.remindAt} sent={!!note.remindedAt} onClose={() => setPicking(null)} onSet={iso => change(note.id, { remindAt: iso, remindedAt: null })} />}
        </span>
        {/* 3.76.0: a colour label */}
        <span data-pop style={{ position: 'relative' }}>
          <button onClick={() => setPicking(picking === 'color' ? null : 'color')} style={tool} aria-expanded={picking === 'color'} title="Colour label">{note.color ? <Dot c={note.color} size={11} /> : <Emo e="🎨" />} Colour</button>
          {picking === 'color' && <div role="menu" aria-label="Colour label" style={{ ...POP, right: 0, top: '100%', width: 150 }} onKeyDown={e => { if (e.key === 'Escape') { e.stopPropagation(); setPicking(null); } }}>
            {NOTE_COLORS.map(([k, label]) => <button key={k} role="menuitemradio" aria-checked={note.color === k} onClick={() => { change(note.id, { color: k }); setPicking(null); }} style={{ ...MENU_ITEM, fontWeight: note.color === k ? 700 : 400 }}><Dot c={k} size={12} /> {label}</button>)}
            <button role="menuitemradio" aria-checked={!note.color} onClick={() => { change(note.id, { color: '' }); setPicking(null); }} style={{ ...MENU_ITEM, color: 'var(--t-78909c)' }}>No colour</button>
          </div>}
        </span>
        <button onClick={() => change(note.id, { pinned: !note.pinned })} style={tool} aria-pressed={!!note.pinned}><Emo e="📌" /> {note.pinned ? 'Pinned' : 'Pin'}</button>
        {/* 3.76.0: Duplicate, template, text ↔ checklist */}
        <span data-pop style={{ position: 'relative' }}>
          <button onClick={() => setPicking(picking === 'more' ? null : 'more')} style={tool} aria-expanded={picking === 'more'} aria-label="More">⋯ More</button>
          {picking === 'more' && <div role="menu" aria-label="More" style={{ ...POP, right: 0, top: '100%', width: 230 }} onKeyDown={e => { if (e.key === 'Escape') { e.stopPropagation(); setPicking(null); } }}>
            <button role="menuitem" onClick={() => { setPicking(null); copy(note); }} style={MENU_ITEM}>⧉ Duplicate</button>
            <button role="menuitem" onClick={() => { setPicking(null); change(note.id, { template: !note.template }); notify({ tone: 'success', title: note.template ? 'No longer a template' : 'Saved as a template', detail: note.template ? undefined : 'Start new notes from it with ＋ From a template.' }); }} style={MENU_ITEM}><Emo e="📋" /> {note.template ? 'Stop using as a template' : 'Use as a template'}</button>
            <button role="menuitem" onClick={() => { setPicking(null); convert(note); }} style={MENU_ITEM}>{note.kind === 'list' ? '¶ Make it text' : <><Emo e="☑" /> Make it a checklist</>}</button>
          </div>}
        </span>
        <button onClick={() => remove(note)} style={{ ...MD.danger, padding: '6px 10px', fontSize: 12 }}>Delete</button>
        </>}
      </div>
      {!mine && <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--t-1565c0)', background: 'var(--c-e3f2fd)', borderRadius: 8, padding: '6px 10px' }}>
        <Emo e="👥" /> Shared with you by <strong>{note.sharedBy?.name || note.sharedBy?.email}</strong> · {readOnly ? 'you can view it' : 'you can view and change it'}
      </div>}
      {sharing && <ShareBox note={note} getToken={getToken} notify={notify} onSaved={sharesSaved} onClose={() => setSharing(false)} />}
      {note.template && <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12.5, color: 'var(--t-546e7a)', background: 'var(--c-f3e5f5)', borderRadius: 8, padding: '6px 10px' }}>
        <span><Emo e="📋" /> This note is a template. New notes made from it start with all items unticked and no reminders.</span>
        <button onClick={() => copy(note, true)} style={{ ...tool, padding: '4px 10px' }}>Use this template</button>
      </div>}
      {note.remindAt && <div style={{ marginTop: 8 }}>{chip(note.remindAt, !!note.remindedAt)}</div>}
      {/* 3.76.0: faxes added from the fax panel */}
      {note.links?.length > 0 && <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }} aria-label="Faxes in this note">
        {note.links.map(l => (
          <span key={`${l.type}:${l.faxId}`} style={{ display: 'inline-flex', alignItems: 'center', background: 'var(--c-e3f2fd)', borderRadius: 14, fontSize: 12 }}>
            <button onClick={() => openFax(l)} title="Open this fax" style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 600, color: 'var(--t-1565c0)', padding: '3px 4px 3px 9px' }}><Emo e={l.type === 'incoming' ? '📥' : '📤'} /> {faxLinkText(l)}</button>
            <button onClick={() => change(note.id, { links: note.links.filter(x => !(x.type === l.type && x.faxId === l.faxId)) })} aria-label="Take this fax out of the note" title="Take it out of the note" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--t-90a4ae)', fontSize: 12, padding: '3px 8px 3px 2px' }}>✕</button>
          </span>
        ))}
      </div>}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', marginTop: 10 }}>
        {note.kind === 'list' ? (
          <>
            {open.map(row)}
            {!readOnly && <div onDragOver={e => { if (dragId) e.preventDefault(); }} onDrop={e => { e.preventDefault(); if (dragId) moveTo(dragId, done[0]?.id || null); setDragId(null); }}>
              <button onClick={() => addAfter(items.length - 1)} style={{ background: 'none', border: 'none', color: 'var(--t-00897b)', cursor: 'pointer', fontWeight: 700, fontSize: 13, padding: '8px 2px', fontFamily: 'inherit' }}>＋ Add item</button>
            </div>}
            {done.length > 0 && <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                <div style={{ ...MD.label, margin: 0 }}>Done ({done.length})</div>
                <span style={{ flex: 1 }} />
                {!readOnly && <>
                <button onClick={() => setItems(items.map(x => ({ ...x, done: false })))} style={tool} title="Untick everything, to use this checklist again">Untick all</button>
                <button onClick={() => clearTicked(note)} style={tool} title="Remove the ticked items (Undo brings them back)">Clear ticked items</button>
                </>}
              </div>
              {done.map(row)}
            </>}
          </>
        ) : readOnly ? (
          // 3.77.0: shared with you to view: the text as written (the server keeps only safe formatting)
          <div className="note-rich"><div className="et-rich" style={{ fontSize: 15, lineHeight: 1.6, padding: '6px 2px' }} dangerouslySetInnerHTML={{ __html: note.html || fromPlain(note.text) }} /></div>
        ) : (
          // 3.76.0: bold, italic, underline and lists (Ctrl+B / I / U work too)
          <div className="note-rich">
            <style>{'.note-rich .et-rich{font-size:15px;line-height:1.6}.note-rich .et-rich ul,.note-rich .et-rich ol{margin:0 0 6px;padding-left:22px}'}</style>
            <span id={`note-text-${note.id}`} style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>Note</span>
            <RichText key={`${note.id}:${rev}`} labelId={`note-text-${note.id}`} initial={note.html || fromPlain(note.text)} placeholders={false} pictures={false} links={false} cleanPaste={cleanPaste}
              onChange={html => { const text = toText(html); if (text.length <= 20000) change(note.id, { html, text }); }} minHeight={260} />
          </div>
        )}
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--t-b0bec5)', marginTop: 6 }}>{readOnly ? 'View only' : 'Saved by itself as you type'} · last changed {formatShortDate(note.updatedAt)}</div>
    </>
  );
}
